import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

const scopLabels = {
  personal: "necesitati personale",
  medical: "cheltuieli medicale",
  studii: "studii / educatie",
  afacere: "investitie / afacere",
  altele: "scopuri personale",
};

const indiceLabels = {
  IRCC: "IRCC",
  EURIBOR_3M: "EURIBOR 3M",
  EURIBOR_6M: "EURIBOR 6M",
  SOFR_3M: "SOFR 3M",
};

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());

  const dataImprumutFmt = data.dataImprumut
    ? fmtDate(new Date(data.dataImprumut))
    : "___";

  const ciImprumutatorFmt = !data.imprumutatorPJ && data.imprumutatorCI ? formatCI(data.imprumutatorCI) : "___ ___";
  const ciImprumutatFmt = !data.imprumutatPJ && data.imprumutatCI ? formatCI(data.imprumutatCI) : "___ ___";

  const suma = data.sumaImprumut ? Number(data.sumaImprumut) : 0;
  const valuta = data.valuta || "RON";
  const nrRate = parseInt(data.nrRate) || 12;
  const sumaRata = data.modalitateRestituire === "rate" && suma > 0
    ? (suma / nrRate).toFixed(2)
    : null;

  // Genereaza datele scadente (ultima zi a fiecarei luni)
  const genereazaScadente = () => {
    if (!data.dataImprumut) return [];
    const startDate = new Date(data.dataImprumut);
    const scadente = [];
    for (let i = 1; i <= nrRate; i++) {
      const year = startDate.getFullYear();
      const month = startDate.getMonth() + i;
      // Ultima zi a lunii: ziua 0 a lunii urmatoare
      const ultimaZi = new Date(year, month + 1, 0);
      scadente.push(fmtDate(ultimaZi));
    }
    return scadente;
  };
  const dateScadente = genereazaScadente();

  // Data restituirii: pentru rate = ultima scadenta, pentru integral = data din formular
  const dataRestituireFmt = data.modalitateRestituire === "rate"
    ? (dateScadente.length > 0 ? dateScadente[dateScadente.length - 1] : "___")
    : (data.dataRestituire ? fmtDate(new Date(data.dataRestituire)) : "___");

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };
  const fieldRow = { ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" };
  const bullet = { ...p, paddingLeft: isMobile ? "20px" : "36px", margin: "2px 0" };

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Contract de Imprumut
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: "4px 0 0 0",
        }}>
          (intre persoane fizice / juridice)
        </p>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: "4px 0 0 0",
        }}>
          Nr. ___ din {today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE CONTRACTANTE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE CONTRACTANTE</h3>

        <p style={p}><span style={bold}>1.1. Imprumutatorul:</span></p>
        {data.imprumutatorPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.imprumutatorFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.imprumutatorCUI || "________________________"}</span></p>
            {data.imprumutatorRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.imprumutatorRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.imprumutatorSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.imprumutatorReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.imprumutatorNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.imprumutatorCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciImprumutatorFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.imprumutatorAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>

        <p style={p}><span style={bold}>1.2. Imprumutatul:</span></p>
        {data.imprumutatPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.imprumutatFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.imprumutatCUI || "________________________"}</span></p>
            {data.imprumutatRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.imprumutatRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.imprumutatSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.imprumutatReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.imprumutatNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.imprumutatCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciImprumutatFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.imprumutatAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, fontStyle: "italic", marginTop: "16px" }}>
          au convenit incheierea prezentului contract de imprumut, in conformitate cu dispozitiile art. 2158-2170 din Codul Civil, in urmatoarele conditii:
        </p>
      </div>

      {/* II. OBIECTUL CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. OBIECTUL CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Imprumutatorul acorda Imprumutatului un imprumut in suma de <span style={bold}>{suma > 0 ? suma.toLocaleString("ro-RO") : "___"} {valuta}</span>. Suma imprumutata este destinata utilizarii de catre Imprumutat pentru scopul declarat: <span style={bold}>{scopLabels[data.scopImprumut] || "necesitati personale"}</span>, fara a afecta obligatia de restituire a sumei.
        </p>
        <p style={p}>
          <span style={bold}>2.2.</span> Suma imprumutata va fi remisa Imprumutatului in numerar / prin transfer bancar, la data semnarii prezentului contract sau la data de <span style={bold}>{dataImprumutFmt}</span>.
        </p>
        <p style={p}>
          <span style={bold}>2.3.</span> Imprumutatul confirma primirea sumei mentionate la art. 2.1 si se obliga sa o restituie in conditiile prevazute in prezentul contract.
        </p>
      </div>

      {/* III. DURATA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. DURATA CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>3.1.</span> Imprumutul se acorda incepand cu data de <span style={bold}>{dataImprumutFmt}</span> si trebuie restituit {data.modalitateRestituire === "rate" ? <>in <span style={bold}>{nrRate} rate lunare</span>, conform prevederilor art. V</> : <>integral pana la data de <span style={bold}>{dataRestituireFmt}</span></>}.
        </p>
        <p style={p}>
          <span style={bold}>3.2.</span> Imprumutatul poate restitui suma imprumutata si inainte de termenul prevazut, fara penalitati de rambursare anticipata.
        </p>
      </div>

      {/* IV. DOBANDA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. DOBANDA</h3>
        {data.tipDobanda === "fara" && (
          <p style={p}>
            <span style={bold}>4.1.</span> Prezentul imprumut este acordat fara dobanda, cu titlu gratuit.
          </p>
        )}
        {data.tipDobanda === "fixa" && (
          <>
            <p style={p}>
              <span style={bold}>4.1.</span> Imprumutul este purtator de dobanda <span style={bold}>fixa</span> in cuantum de <span style={bold}>{data.dobandaProcent || "___"}% pe an</span>.
            </p>
            <p style={p}>
              <span style={bold}>4.2.</span> Dobanda se calculeaza de la data acordarii imprumutului si se achita {data.modalitateRestituire === "rate" ? "lunar, odata cu rata" : "la data restituirii integrale a sumei imprumutate"}.
            </p>
          </>
        )}
        {data.tipDobanda === "variabila" && (
          <>
            <p style={p}>
              <span style={bold}>4.1.</span> Imprumutul este purtator de dobanda <span style={bold}>variabila</span>, calculata dupa formula: <span style={bold}>{indiceLabels[data.dobandaIndice] || "IRCC"} + {data.dobandaMarja || "___"}% marja fixa</span>.
            </p>
            <p style={p}>
              <span style={bold}>4.2.</span> Indicele de referinta <span style={bold}>{indiceLabels[data.dobandaIndice] || "IRCC"}</span> este cel publicat de {data.valuta === "EUR" ? "Banca Centrala Europeana" : data.valuta === "USD" ? "Federal Reserve" : "Banca Nationala a Romaniei"} la data scadentei fiecarei plati.
            </p>
            <p style={p}>
              <span style={bold}>4.3.</span> Dobanda totala (indice + marja) nu poate depasi plafonul maxim de <span style={bold}>{data.dobandaPlafon || "___"}% pe an</span>, indiferent de evolutia indicelui de referinta.
            </p>
            <p style={p}>
              <span style={bold}>4.4.</span> Dobanda se calculeaza asupra soldului restant si se achita {data.modalitateRestituire === "rate" ? "concomitent cu fiecare rata, in cuantumul rezultat conform prezentului articol" : "la data restituirii integrale a sumei imprumutate, calculata pentru intreaga perioada"}.
            </p>
          </>
        )}
      </div>

      {/* V. RESTITUIREA IMPRUMUTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>V. RESTITUIREA IMPRUMUTULUI</h3>
        {data.modalitateRestituire === "rate" ? (
          <>
            <p style={p}>
              <span style={bold}>5.1.</span> Restituirea sumei imprumutate se va face in <span style={bold}>{nrRate} rate lunare egale</span>, in cuantum de <span style={bold}>{sumaRata ? `${sumaRata} ${valuta}` : "___"} / rata</span> (fara dobanda).
            </p>
            <p style={p}>
              <span style={bold}>5.2.</span> Ratele sunt scadente in ultima zi a fiecarei luni, dupa cum urmeaza: <span style={bold}>{dateScadente.length > 0 ? dateScadente.join(", ") : "___"}</span>.
            </p>
            <p style={p}>
              <span style={bold}>5.3.</span> Plata se va efectua prin transfer bancar in contul Imprumutatorului sau in numerar, cu confirmare scrisa de primire, cu respectarea legislatiei privind plafonul platilor in numerar.
            </p>
          </>
        ) : (
          <>
            <p style={p}>
              <span style={bold}>5.1.</span> Restituirea integrala a sumei imprumutate de <span style={bold}>{suma > 0 ? suma.toLocaleString("ro-RO") : "___"} {valuta}</span> se va face intr-o singura transa, la data scadentei (<span style={bold}>{dataRestituireFmt}</span>).
            </p>
            <p style={p}>
              <span style={bold}>5.2.</span> Plata se va efectua prin transfer bancar in contul Imprumutatorului sau in numerar, cu confirmare scrisa de primire, cu respectarea legislatiei privind plafonul platilor in numerar.
            </p>
          </>
        )}
      </div>

      {/* VI. PENALITATI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VI. PENALITATI</h3>
        {data.penalizareActiva ? (
          <>
            <p style={p}>
              <span style={bold}>6.1.</span> In cazul nerestituirii la termen a sumei imprumutate (sau a ratelor lunare), Imprumutatul datoreaza penalitati de intarziere in cuantum de <span style={bold}>{data.penalizare || "___"}% pe zi</span> din suma restanta, fara a depasi cuantumul debitului.
            </p>
            <p style={p}>
              <span style={bold}>6.2.</span> Penalitatile curg de drept, fara punere in intarziere si fara nicio alta formalitate prealabila.
            </p>
          </>
        ) : (
          <p style={p}>
            <span style={bold}>6.1.</span> Partile au convenit ca nu se percep penalitati de intarziere. In cazul nerestituirii la termen, Imprumutatorul poate solicita restituirea sumei pe cale amiabila sau judiciara.
          </p>
        )}
      </div>

      {/* VII. FIDEJUSIUNE */}
      {data.garantieActiva && (
        <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
          <h3 style={sectionTitle()}>VII. FIDEJUSIUNE</h3>
          <p style={p}>
            <span style={bold}>7.1.</span> Restituirea sumei imprumutate este garantata prin fidejusiunea acordata de:
          </p>
          <p style={fieldRow}><span style={bold}>Nume:</span> {data.garantNume || "___"}</p>
          <p style={fieldRow}><span style={bold}>CNP:</span> {data.garantCNP || "___"}</p>
          <p style={fieldRow}><span style={bold}>CI:</span> {data.garantCI || "___"}</p>
          <p style={fieldRow}><span style={bold}>Domiciliu:</span> {data.garantAdresa || "___"}</p>
          <p style={{ ...p, marginTop: "12px" }}>
            <span style={bold}>7.2.</span> Fidejusorul se obliga {data.garantSolidar !== false ? <><span style={bold}>solidar</span> cu Imprumutatul</> : "subsidiar fata de Imprumutat"} sa restituie Imprumutatorului suma imprumutata, dobanzile si penalitatile datorate, in cazul in care Imprumutatul nu isi indeplineste obligatiile la termen.
          </p>
          {data.garantSolidar !== false && (
            <p style={p}>
              <span style={bold}>7.3.</span> In virtutea solidaritatii, Imprumutatorul poate urmari direct Fidejusorul pentru intreaga datorie, fara a fi obligat sa se indrepte mai intai impotriva Imprumutatului.
            </p>
          )}
          <p style={p}>
            <span style={bold}>7.{data.garantSolidar !== false ? "4" : "3"}.</span> Fidejusorul declara ca a luat cunostinta de continutul prezentului contract si ca isi asuma in mod expres obligatia de garantare.
          </p>
        </div>
      )}

      {/* VIII. OBLIGATIILE PARTILOR */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "VIII" : "VII"}. OBLIGATIILE PARTILOR</h3>

        <p style={p}><span style={bold}>Obligatiile Imprumutatorului:</span></p>
        {[
          "Sa remita Imprumutatului suma convenita, la data si in modalitatea stabilite.",
          "Sa nu solicite restituirea sumei inainte de termenul convenit, cu exceptia cazurilor prevazute de lege.",
        ].map((text, i) => (
          <p key={i} style={bullet}>- {text}</p>
        ))}

        <p style={{ ...p, marginTop: "12px" }}><span style={bold}>Obligatiile Imprumutatului:</span></p>
        {[
          "Sa restituie suma imprumutata la termenul si in conditiile stabilite prin prezentul contract.",
          "Scopul imprumutului are caracter declarativ si nu afecteaza obligatia de restituire.",
          ...(data.tipDobanda !== "fara" ? ["Sa achite dobanda la termenele stabilite, conform art. IV."] : []),
          "Sa informeze Imprumutatorul despre orice situatie care ar putea afecta capacitatea de rambursare.",
        ].map((text, i) => (
          <p key={i} style={bullet}>- {text}</p>
        ))}
      </div>

      {/* IX. INCETAREA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "IX" : "VIII"}. INCETAREA CONTRACTULUI</h3>
        <p style={p}><span style={bold}>{data.garantieActiva ? "9" : "8"}.1.</span> Contractul inceteaza:</p>
        {[
          "prin restituirea integrala a sumei imprumutate si a dobanzilor, daca este cazul",
          "la data scadentei",
          "prin acordul scris al partilor",
          "prin reziliere, in cazul nerespectarii obligatiilor contractuale esentiale",
          "prin rambursare anticipata, la initiativa Imprumutatului",
        ].map((text, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcde"[i]})</span> {text}
          </p>
        ))}
      </div>

      {/* X. LITIGII */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "X" : "IX"}. LITIGII</h3>
        <p style={indent}>
          <span style={bold}>{data.garantieActiva ? "10" : "9"}.1.</span> Litigiile decurgand din executarea prezentului contract se solutioneaza pe cale amiabila, iar in caz contrar de catre instantele judecatoresti competente.
        </p>
      </div>

      {/* XI. DISPOZITII FINALE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "XI" : "X"}. DISPOZITII FINALE</h3>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "11" : "10"}.1.</span> Prezentul contract reprezinta un inscris sub semnatura privata si nu constituie titlu executoriu.</p>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "11" : "10"}.2.</span> Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti.</p>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "11" : "10"}.3.</span> Contractul se incheie in 2 (doua) exemplare originale, cate unul pentru fiecare parte{data.martori ? ", plus cate un exemplar pentru fiecare martor" : ""}.</p>
      </div>

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
        flexWrap: "wrap",
        gap: "24px",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            IMPRUMUTATOR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.imprumutatorPJ
              ? (data.imprumutatorFirma ? `${data.imprumutatorFirma} prin ${data.imprumutatorReprezentant || ""}` : "")
              : (data.imprumutatorNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            IMPRUMUTAT
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.imprumutatPJ
              ? (data.imprumutatFirma ? `${data.imprumutatFirma} prin ${data.imprumutatReprezentant || ""}` : "")
              : (data.imprumutatNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>

      {/* Fidejusor */}
      {data.garantieActiva && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "32px",
          paddingTop: "24px",
          borderTop: "1px dashed #d4cdc0",
          pageBreakInside: "avoid",
          breakInside: "avoid",
        }}>
          <div style={{ textAlign: "center", flex: "0 0 50%" }}>
            <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              FIDEJUSOR
            </p>
            <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
            <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
              {data.garantNume || ""}
            </p>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
              Data: {today}
            </p>
          </div>
        </div>
      )}

      {/* Martori */}
      {data.martori && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "32px",
          paddingTop: "24px",
          borderTop: "1px dashed #d4cdc0",
          pageBreakInside: "avoid",
          breakInside: "avoid",
          flexWrap: "wrap",
          gap: "24px",
        }}>
          <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              MARTOR 1
            </p>
            <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>Nume: ________________________</p>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>CNP: ________________________</p>
          </div>
          <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              MARTOR 2
            </p>
            <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>Nume: ________________________</p>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>CNP: ________________________</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default ContractPreview;
